import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import get from 'lodash/get';
import { FEATURE_TAGS, FEATURE_VULNS, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import Button from 'shared/ui/Button';
import { Tag } from '../../styled';
import { setCreationTagModal } from 'Screens/Contextualization/Tags/actions/Actions';
import { useIntl } from 'react-intl';
import ModalWarning from 'Common/Components/ModalWarning';
import { openModal } from 'store/modals/actions';
import { MODAL_UPGRADE_LICENSE_ACTION_BAR } from 'store/modals/modals';
import FFTags from 'Images/ff_tags.png';

const TagButton = ({
  setFF,
  hasItems,
  vulnsSelected = [],
  tagsEntity,
  isExploreAssetsTab,
  isExploreServicesTab,
  selectedHostIds = [],
  selectedServiceIds = []
}) => {
  const [showWarning, setShowWarning] = useState(false);
  const dispatch = useDispatch();
  const readonly = useSelector((state) => get(state, 'faraday.readonly', false));
  const canCreateTags = useSelector((state) => selectAllowedByRole(state, FEATURE_TAGS, PERMISSION_TYPES.CREATE));
  const canPatchVulns = useSelector((state) => selectAllowedByRole(state, FEATURE_VULNS, PERMISSION_TYPES.PATCH));
  const canUpdate = tagsEntity === 'vulnsGeneralMenu'
    ? canCreateTags && canPatchVulns && hasItems
    : !readonly && canCreateTags && canPatchVulns && hasItems;
  const intl = useIntl();

  const getWarningMessage = () => {
    if (isExploreAssetsTab) return intl.formatMessage({ id: 'modalWarning.selectOneOrMore' }, { entity: 'assets' });
    if (isExploreServicesTab) return intl.formatMessage({ id: 'modalWarning.selectOneOrMore' }, { entity: 'services' });
    return intl.formatMessage({ id: 'modalWarning.selectOneOrMore' }, { entity: 'vulnerabilities' });
  };

  const handleClick = () => {
    if (!canUpdate) return;

    if (isExploreAssetsTab) {
      if (selectedHostIds.length > 0) dispatch(setCreationTagModal(true, 'exploreGroupHosts'));
      else setShowWarning(true);
      return;
    }

    if (isExploreServicesTab) {
      if (selectedServiceIds.length > 0) dispatch(setCreationTagModal(true, 'exploreGroupServices'));
      else setShowWarning(true);
      return;
    }

    if (vulnsSelected.length > 0) dispatch(setCreationTagModal(true, tagsEntity));
    else setShowWarning(true);
  };

  const onClose = () => setShowWarning(false);

  // Community has no backend support for the full Tags feature at all (see
  // c8c235df2), but the plain "Tags" icon itself is a real, pre-existing part of
  // community/dev: it's always shown (disabled until something is selected) and just
  // opens the same upgrade-license prompt used by the rest of the action bar, instead of
  // disappearing outright.
  const onClickLocked = () => {
    if (hasItems) {
      setFF(FFTags, 'upgradeLicense.sidebar.tags.description');
      dispatch(openModal(MODAL_UPGRADE_LICENSE_ACTION_BAR));
    }
  };

  if (!canCreateTags) {
    return <Button variant="ghost" startIcon={ <Tag /> } title="Tags" ariaLabel="Tags" onClick={ onClickLocked } disabled={ !hasItems } />;
  }

  return (
    <>
      <Button variant="ghost" startIcon={ <Tag /> } title="Tags" ariaLabel="Tags" onClick={ handleClick } disabled={ !canUpdate } />
      { showWarning && <ModalWarning onCloseCallback={ onClose } title="Tags" description={ getWarningMessage() } /> }
    </>
  );
};

export default TagButton;
